// Game-of-24 solver: enumerate every winning expression for a set of four numbers.
//
// We don't try to work out what the player attempted — we generate the full list of
// VALID answers and (elsewhere, in spokenMatch.ts) fuzzy-search each one through the
// transcript. Enumeration is deterministic and bounded: 4! number orders × 4³ operator
// triples × 5 parenthesisations ≈ a few thousand candidates, each checked with the
// exact-rational judge. Results are memoised per number-set.

import { judgeGame24 } from './evaluate'

const OPS = ['+', '-', '*', '/'] as const

// All orderings of the four numbers (deduped when numbers repeat).
const orderings = (nums: number[]): number[][] => {
  const res: number[][] = []
  const seen = new Set<string>()
  const used = new Array(nums.length).fill(false)
  const cur: number[] = []
  const go = () => {
    if (cur.length === nums.length) {
      const key = cur.join(',')
      if (!seen.has(key)) {
        seen.add(key)
        res.push([...cur])
      }
      return
    }
    for (let i = 0; i < nums.length; i++) {
      if (used[i]) continue
      used[i] = true
      cur.push(nums[i])
      go()
      cur.pop()
      used[i] = false
    }
  }
  go()
  return res
}

// The 5 ways to parenthesise a o b o c o d.
const parenthesisations = (a: string, b: string, c: string, d: string, o: string[]): string[] => [
  `((${a}${o[0]}${b})${o[1]}${c})${o[2]}${d}`,
  `(${a}${o[0]}(${b}${o[1]}${c}))${o[2]}${d}`,
  `(${a}${o[0]}${b})${o[1]}(${c}${o[2]}${d})`,
  `${a}${o[0]}((${b}${o[1]}${c})${o[2]}${d})`,
  `${a}${o[0]}(${b}${o[1]}(${c}${o[2]}${d}))`,
]

export type Solution = { expr: string; signature: string[] }

// Operand/operator token sequence with parentheses stripped — what we match on.
export const signatureOf = (expr: string): string[] => expr.match(/\d+|[+\-*/]/g) || []

const cache = new Map<string, Solution[]>()

export const solveAll = (numbers: number[]): Solution[] => {
  const key = [...numbers].sort((a, b) => a - b).join(',')
  const cached = cache.get(key)
  if (cached) return cached

  const out: Solution[] = []
  const seenSig = new Set<string>()
  for (const order of orderings(numbers)) {
    const [a, b, c, d] = order.map(String)
    for (const o0 of OPS)
      for (const o1 of OPS)
        for (const o2 of OPS) {
          for (const expr of parenthesisations(a, b, c, d, [o0, o1, o2])) {
            if (!judgeGame24(expr, numbers).correct) continue
            const sig = signatureOf(expr)
            const sigKey = sig.join(' ')
            if (seenSig.has(sigKey)) continue
            seenSig.add(sigKey)
            out.push({ expr, signature: sig })
          }
        }
  }
  cache.set(key, out)
  return out
}

// A clean worked example to show the player — the shortest valid expression.
export const exampleSolution = (numbers: number[]): string | null => {
  const all = solveAll(numbers)
  if (all.length === 0) return null
  return all.map((s) => s.expr).sort((a, b) => a.length - b.length || a.localeCompare(b))[0]
}
